/**
 * ReviewCompleteCard - Displays PR review completion results
 * 
 * Shows verdict, summary, issues found, and constraint verification results.
 * Resilient to malformed LLM JSON - falls back to displaying raw data.
 */
import { cn } from '@/lib/utils'
import { CheckCircle, XCircle, AlertTriangle, Check, X, Code } from 'lucide-react'

// Structured issue object from backend
interface IssueObject {
  severity?: string
  category?: string
  file?: string
  line?: number
  description?: string
  comment_posted?: boolean
}

// Issues can be strings or structured objects
type Issue = string | IssueObject

export interface ReviewCompleteData {
  approved?: boolean
  verdict?: string
  summary?: string
  high_severity_issues?: Array<Issue>
  medium_low_issues?: Array<Issue>
  comments_posted?: number
  files_reviewed?: number
  lines_changed?: number | null
  constraints_checked?: Record<string, { satisfied: boolean; details: string }>
}

// Helper to safely extract display text from an issue
function getIssueText(issue: Issue): string {
  // Handle null/undefined
  if (issue == null) {
    return '(empty issue)'
  }
  
  // Handle strings
  if (typeof issue === 'string') {
    return issue
  }
  
  // Handle non-objects (numbers, booleans, etc.)
  if (typeof issue !== 'object') {
    return String(issue)
  }
  
  // Structured issue object - try to extract meaningful text
  try {
    const parts: string[] = []
    if (issue.file) {
      parts.push(issue.line ? `${issue.file}:${issue.line}` : String(issue.file))
    }
    if (issue.description) {
      parts.push(String(issue.description))
    }
    // If we got something, return it
    if (parts.length > 0) {
      return parts.join(' - ')
    }
    // Otherwise stringify the whole object
    return JSON.stringify(issue)
  } catch {
    return '[Unable to parse issue]'
  }
}

// Helper to safely convert to array
function toSafeArray(value: unknown): Issue[] {
  if (Array.isArray(value)) {
    return value
  }
  if (value == null) {
    return []
  }
  // If it's a single item, wrap in array
  return [value as Issue]
}

// Helper to safely get string
function toSafeString(value: unknown, fallback: string = ''): string {
  if (typeof value === 'string') return value
  if (value == null) return fallback
  try {
    return String(value)
  } catch {
    return fallback
  }
}

// Helper to safely get boolean
function toSafeBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true' || value.toLowerCase() === 'approved'
  }
  return Boolean(value)
}

export interface ReviewCompleteCardProps {
  data: ReviewCompleteData | unknown  // Accept unknown for resilience
  reasoning?: string
  className?: string
}

// Fallback component when data is completely unparseable
function FallbackDisplay({ data, className }: { data: unknown; className?: string }) {
  return (
    <div className={cn("rounded-lg border bg-card p-4 space-y-3", className)}>
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-full bg-muted">
          <Code className="h-5 w-5 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base">Review Result</h3>
          <p className="text-xs text-muted-foreground mt-1">Unable to parse structured data</p>
        </div>
      </div>
      <pre className="text-xs bg-muted/50 rounded p-3 overflow-auto max-h-64 whitespace-pre-wrap break-words">
        {typeof data === 'string' ? data : JSON.stringify(data, null, 2)}
      </pre>
    </div>
  )
}

export function ReviewCompleteCard({ data, reasoning, className }: ReviewCompleteCardProps) {
  // Handle null/undefined data
  if (data == null) {
    return <FallbackDisplay data="No review data available" className={className} />
  }

  // Handle non-object data (string, number, etc.)
  if (typeof data !== 'object') {
    return <FallbackDisplay data={data} className={className} />
  }

  // Safely extract fields with fallbacks
  const safeData = data as ReviewCompleteData
  
  const verdict = toSafeString(safeData.verdict, 'Review Complete')
  const summary = toSafeString(safeData.summary, '')
  const approved = safeData.approved != null 
    ? toSafeBoolean(safeData.approved)
    : verdict.toLowerCase() === 'approved'
  
  const high_severity_issues = toSafeArray(safeData.high_severity_issues)
  const medium_low_issues = toSafeArray(safeData.medium_low_issues)
  const comments_posted = typeof safeData.comments_posted === 'number' ? safeData.comments_posted : undefined
  const files_reviewed = typeof safeData.files_reviewed === 'number' ? safeData.files_reviewed : undefined
  const constraints_checked = safeData.constraints_checked && typeof safeData.constraints_checked === 'object'
    ? safeData.constraints_checked
    : undefined
  
  const isApproved = approved || verdict.toLowerCase() === 'approved'
  const needsWork = verdict.toLowerCase().includes('needs') || verdict.toLowerCase().includes('reject')
  
  return (
    <div className={cn("rounded-lg border bg-card p-4 space-y-3", className)}>
      <div className="flex items-start gap-3">
        <div className={cn(
          "p-2 rounded-full",
          isApproved ? "bg-green-100 dark:bg-green-900/30" : 
          needsWork ? "bg-red-100 dark:bg-red-900/30" : 
          "bg-yellow-100 dark:bg-yellow-900/30"
        )}>
          {isApproved ? (
            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
          ) : needsWork ? (
            <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
          ) : (
            <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-base">Review Complete</h3>
            <span className={cn(
              "px-2 py-0.5 rounded-full text-xs font-medium uppercase",
              isApproved ? "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300" :
              needsWork ? "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300" :
              "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300"
            )}>
              {verdict}
            </span>
          </div>
          {/* Stats */}
          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
            {files_reviewed !== undefined && (
              <span>{files_reviewed} file{files_reviewed !== 1 ? 's' : ''} reviewed</span>
            )}
            {comments_posted !== undefined && comments_posted > 0 && (
              <span>{comments_posted} comment{comments_posted !== 1 ? 's' : ''}</span>
            )}
          </div>
        </div>
      </div>
      
      {/* Summary */}
      {summary && <p className="text-sm">{summary}</p>}
      
      {/* High severity issues */}
      {high_severity_issues.length > 0 && (
        <div className="space-y-1">
          <h4 className="text-sm font-medium text-red-600 dark:text-red-400">High Severity Issues</h4>
          <ul className="space-y-1">
            {high_severity_issues.map((issue, idx) => (
              <li key={idx} className="text-sm text-red-600 dark:text-red-400 flex items-start gap-2">
                <span className="mt-1">•</span>
                <span>{getIssueText(issue)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Medium/Low issues */}
      {medium_low_issues.length > 0 && (
        <div className="space-y-1">
          <h4 className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Other Issues</h4>
          <ul className="space-y-1">
            {medium_low_issues.map((issue, idx) => (
              <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                <span className="mt-1">•</span>
                <span>{getIssueText(issue)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Constraints checked */}
      {constraints_checked && Object.keys(constraints_checked).length > 0 && (
        <div className="space-y-2 pt-2 border-t">
          <h4 className="text-xs font-medium text-muted-foreground">Constraints Verified</h4>
          <div className="space-y-1">
            {Object.entries(constraints_checked).map(([constraint, result]) => {
              // Safely handle malformed constraint results
              const satisfied = result && typeof result === 'object' 
                ? toSafeBoolean(result.satisfied)
                : false
              return (
                <div key={constraint} className="flex items-start gap-2 text-xs">
                  {satisfied ? (
                    <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <X className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <span className="font-mono text-muted-foreground">
                      {String(constraint).split(':').slice(0, 2).join(':')}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
      
      {/* Reasoning */}
      {reasoning && (
        <div className="pt-2 border-t">
          <p className="text-xs text-muted-foreground">{reasoning}</p>
        </div>
      )}
    </div>
  )
}
